/* eslint-disable camelcase */
import { useEffect, useState, useRef } from "react"
import { useQueryClient } from "@tanstack/react-query"
import Header from "../../../layouts/Header"
import BackIcon from "../../../components/BackIcon"
import { useNavigate, useLocation } from "react-router-dom"
import {
  Box,
  Paper,
  TextField,
  Grid,
  Typography,
  Divider,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material"
import { createTheme, ThemeProvider } from "@mui/material/styles"
import CustomBackdrop from "../../../components/CustomBackdrop"
import { useQuery, api } from "../../../api"
import PrintIcon from "@mui/icons-material/Print"
import HistoryIcon from "@mui/icons-material/History"
import CloseIcon from "@mui/icons-material/Close"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import { useReactToPrint } from "react-to-print"

const theme = createTheme({
  palette: { primary: { main: "#196C87" }, secondary: { main: "#2E7D32" } },
})

const StyledRoot = {
  width: "100%",
  maxWidth: "1400px",
  margin: "0 auto",
  padding: "20px",
  backgroundColor: "#f5f7fa",
  borderRadius: "12px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
}

// =======================================================================
// FUNCIONES DE FORMATEO ROBUSTAS DE FECHA Y HORA (Soporta RFC 1123 e ISO)
// =======================================================================
const formatFechaVB6 = (dateStr) => {
  if (!dateStr) return ""
  const str = String(dateStr).trim()
  const d = new Date(str)

  if (isNaN(d.getTime())) return str // Fallback si no es una fecha válida

  // Si el string indica zona horaria (GMT, UTC, Z), usamos métodos UTC para no desplazar el día
  const isUTC = /GM|Z|\+|UTC/.test(str.toUpperCase())

  const day = isUTC ? d.getUTCDate() : d.getDate()
  const monthIdx = isUTC ? d.getUTCMonth() : d.getMonth()
  const year = isUTC ? d.getUTCFullYear() : d.getFullYear()

  const meses = [
    "enero",
    "febrero",
    "marzo",
    "abril",
    "mayo",
    "junio",
    "julio",
    "agosto",
    "septiembre",
    "octubre",
    "noviembre",
    "diciembre",
  ]
  return `${day}/${meses[monthIdx]}/${year}`
}

const formatHora = (timeStr) => {
  if (!timeStr) return ""
  const str = String(timeStr).trim()

  // Si el backend envía solo la hora pura (ej. "16:52:27")
  if (/^\d{2}:\d{2}(:\d{2})?/.test(str)) {
    return str.substring(0, 8)
  }

  const d = new Date(str)
  if (isNaN(d.getTime())) {
    // Intento de rescate mediante Regex si el parseo nativo falla
    const possibleTime = str.match(/\d{2}:\d{2}:\d{2}/)
    return possibleTime ? possibleTime[0] : str.substring(0, 8)
  }

  const isUTC = /GM|Z|\+|UTC/.test(str.toUpperCase())
  const hours = isUTC ? d.getUTCHours() : d.getHours()
  const mins = isUTC ? d.getUTCMinutes() : d.getMinutes()
  const secs = isUTC ? d.getUTCSeconds() : d.getSeconds()

  return `${String(hours).padStart(2, "0")}:${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`
}

// =======================================================================
// COMPONENTE DE REPORTE VB6 (Formato A4 para impresión)
// =======================================================================
const ReporteComprobanteVB6 = ({ transaccion, ciaNombre, solnumero }) => {
  if (!transaccion) return null

  const isIngreso = String(transaccion.tratipo).toUpperCase() === "INGRESO"
  const tituloDocumento = isIngreso ? "INGRESO MERCADERIA" : "EGRESO MERCADERIA"

  const totalCantidades = transaccion.detalle?.reduce((acc, item) => acc + parseFloat(item.tracantidad || 0), 0)

  const now = new Date()
  const fecImpresion = now.toLocaleDateString("es-EC", { day: "2-digit", month: "short", year: "numeric" })
  const horaImpresion = now.toLocaleTimeString("es-EC", { hour12: false })

  const fechaEmision = formatFechaVB6(transaccion.trafecha)
  const horaEmision = formatHora(transaccion.trahora || transaccion.trahorisys)

  return (
    <div style={{ padding: "40px", fontFamily: "Arial, sans-serif", color: "#000", backgroundColor: "#fff" }}>
      {/* ENCABEZADO */}
      <div style={{ textAlign: "center", marginBottom: "20px", borderBottom: "2px solid #000", paddingBottom: "10px" }}>
        <h2 style={{ margin: 0, fontSize: "22px", fontWeight: "bold", textTransform: "uppercase" }}>{ciaNombre}</h2>
        <h3 style={{ margin: "5px 0", fontSize: "18px" }}>{tituloDocumento}</h3>
        <h4 style={{ margin: 0, fontSize: "16px", fontWeight: "bold" }}>COMPROBANTE: {transaccion.tranumero}</h4>
      </div>

      {/* DATOS GENERALES (Dos columnas) */}
      <table style={{ width: "100%", fontSize: "12px", marginBottom: "20px" }}>
        <tbody>
          <tr>
            <td style={{ width: "18%", fontWeight: "bold", paddingBottom: "4px" }}>Transacción</td>
            <td style={{ width: "32%", paddingBottom: "4px" }}>| {transaccion.tranombre || transaccion.tratipo}</td>
            <td style={{ width: "18%", fontWeight: "bold", paddingBottom: "4px" }}>Generado en</td>
            <td style={{ width: "32%", paddingBottom: "4px" }}>| MATRIZ</td>
          </tr>
          <tr>
            <td style={{ fontWeight: "bold", paddingBottom: "4px" }}>Fecha Emisión</td>
            <td style={{ paddingBottom: "4px" }}>| {fechaEmision}</td>
            <td style={{ fontWeight: "bold", paddingBottom: "4px" }}>Bodega {isIngreso ? "Destino" : "Origen"}</td>
            <td style={{ paddingBottom: "4px" }}>| {transaccion.bodcodigo}</td>
          </tr>
          <tr>
            <td style={{ fontWeight: "bold", paddingBottom: "4px" }}>Hora Emisión</td>
            <td style={{ paddingBottom: "4px" }}>| {horaEmision}</td>
            <td style={{ fontWeight: "bold", paddingBottom: "4px" }}>Local {isIngreso ? "Origen" : "Destino"}</td>
            <td style={{ paddingBottom: "4px" }}>| </td>
          </tr>
          <tr>
            <td style={{ fontWeight: "bold", paddingBottom: "4px" }}>Usuario Emisión</td>
            <td style={{ paddingBottom: "4px" }}>| {transaccion.trausuisys}</td>
            <td style={{ fontWeight: "bold", paddingBottom: "4px" }}>Referencia (Sol.)</td>
            <td style={{ paddingBottom: "4px" }}>| {solnumero}</td>
          </tr>
          <tr>
            <td style={{ fontWeight: "bold", paddingBottom: "4px" }}>Origen</td>
            <td style={{ paddingBottom: "4px" }}>| INV</td>
            <td></td>
            <td></td>
          </tr>
          <tr>
            <td style={{ fontWeight: "bold", paddingBottom: "4px" }}>Descripción</td>
            <td colSpan="3" style={{ paddingBottom: "4px" }}>
              | {transaccion.tradescripcion || "OK"}
            </td>
          </tr>
        </tbody>
      </table>

      {/* TABLA DE DETALLE */}
      <table style={{ width: "100%", fontSize: "12px", borderCollapse: "collapse", marginBottom: "10px" }}>
        <thead>
          <tr style={{ borderTop: "1px solid #000", borderBottom: "1px solid #000" }}>
            <th style={{ textAlign: "left", padding: "8px 4px" }}>#</th>
            <th style={{ textAlign: "left", padding: "8px 4px" }}>Código</th>
            <th style={{ textAlign: "left", padding: "8px 4px" }}>Código2</th>
            <th style={{ textAlign: "left", padding: "8px 4px" }}>Descripción</th>
            <th style={{ textAlign: "right", padding: "8px 4px" }}>Precio</th>
            <th style={{ textAlign: "right", padding: "8px 4px" }}>Cantidad</th>
          </tr>
        </thead>
        <tbody>
          {transaccion.detalle?.map((item, idx) => (
            <tr key={idx} style={{ borderBottom: "1px dashed #ccc" }}>
              <td style={{ padding: "6px 4px" }}>{idx + 1}</td>
              <td style={{ padding: "6px 4px" }}>| {item.artcodigo}</td>
              <td style={{ padding: "6px 4px" }}>| </td>
              <td style={{ padding: "6px 4px" }}>| {item.artdescri}</td>
              <td style={{ textAlign: "right", padding: "6px 4px" }}>
                | {Number(item.tracostoinventario || 0).toFixed(4)}
              </td>
              <td style={{ textAlign: "right", padding: "6px 4px" }}>| {Number(item.tracantidad || 0).toFixed(2)}</td>
            </tr>
          ))}
          {/* Fila de Totales */}
          <tr>
            <td colSpan="4"></td>
            <td style={{ textAlign: "right", padding: "10px 4px", fontWeight: "bold" }}>Total |</td>
            <td style={{ textAlign: "right", padding: "10px 4px", fontWeight: "bold" }}>
              {Number(totalCantidades).toFixed(2)}
            </td>
          </tr>
        </tbody>
      </table>

      {/* METADATA INFERIOR */}
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: "11px", marginBottom: "60px" }}>
        <div> </div>
        <div>
          <span style={{ marginRight: "20px" }}>Fech-Impresión | {fecImpresion}</span>
          <span>Hora-Impresión | {horaImpresion}</span>
        </div>
      </div>

      {/* SECCIÓN DE FIRMAS */}
      <table style={{ width: "100%", fontSize: "11px", textAlign: "center", marginTop: "50px" }}>
        <tbody>
          <tr>
            <td style={{ width: "25%" }}>
              <div style={{ borderTop: "1px solid #000", margin: "0 20px", paddingTop: "5px" }}>ELABORADO POR</div>
            </td>
            <td style={{ width: "25%" }}>
              <div style={{ borderTop: "1px solid #000", margin: "0 20px", paddingTop: "5px" }}>REVISADO POR</div>
            </td>
            <td style={{ width: "25%" }}>
              <div style={{ borderTop: "1px solid #000", margin: "0 20px", paddingTop: "5px" }}>RECIBI CONFORME</div>
            </td>
            <td style={{ width: "25%" }}>
              <div style={{ borderTop: "1px solid #000", margin: "0 20px", paddingTop: "5px" }}>COPIA</div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  )
}

const BuscarSolicitudInv = () => {
  const navigate = useNavigate()
  const { state } = useLocation()

  // ESTADOS PARA EL MODAL DE IMPRESIÓN
  const [modalImpresionOpen, setModalImpresionOpen] = useState(false)
  const [transaccionSeleccionada, setTransaccionSeleccionada] = useState(null)

  // EXTRACCIÓN DINÁMICA DEL NOMBRE DE LA COMPAÑÍA DESDE EL JWT
  let ciaNombreActual = "EMPRESA S.A."
  try {
    const jwtDataStr = localStorage.getItem("jwt")
    if (jwtDataStr) {
      const jwtData = JSON.parse(jwtDataStr)
      ciaNombreActual = jwtData?.seleccion?.cliciacianombre || "EMPRESA S.A."
    }
  } catch (error) {
    console.error("Error al leer datos de la compañía:", error)
  }

  // REFERENCIA PARA REACT-TO-PRINT
  const componentRef = useRef()
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: transaccionSeleccionada ? `Comprobante_${transaccionSeleccionada.tranumero}` : "Comprobante",
  })

  useEffect(() => {
    if (!state?.solnumero) {
      navigate(-1)
    }
  }, [state, navigate])

  // =======================================================================
  // 1. CONSULTA DE CABECERA Y ACUMULADOS (Estado Actual)
  // =======================================================================
  const { data: solData, isLoading: loadSol } = useQuery({
    queryKey: ["getDetalleSolicitudInvConsulta", state?.solnumero],
    queryFn: async () => {
      try {
        const payload = { solnumero: state.solnumero }
        const response = await api.post("/IngEgrporSolicitud/getDetalleSolicitudInv", payload)

        let coreData = null
        const r1 = response?.data
        if (r1?.data?.data?.solnumero) coreData = r1.data.data
        else if (r1?.data?.solnumero) coreData = r1.data
        else if (r1?.solnumero) coreData = r1

        return coreData
      } catch (error) {
        console.error("Error al cargar la solicitud:", error)
        return null
      }
    },
    enabled: !!state?.solnumero,
  })

  // =======================================================================
  // 2. CONSULTA DE TRAZABILIDAD CON EXTRACCIÓN ROBUSTA DE ARREGLO
  // =======================================================================
  const { data: rawTrazaData, isLoading: loadTraza } = useQuery({
    queryKey: ["getTrazabilidadSolicitud", state?.solnumero],
    queryFn: async () => {
      try {
        const payload = { solnumero: state.solnumero }
        const response = await api.post("/IngEgrporSolicitud/getTrazabilidadSolicitud", payload)

        const res = response?.data?.data || response?.data || response || []
        return res
      } catch (error) {
        console.error("Error al cargar la trazabilidad:", error)
        return []
      }
    },
    enabled: !!state?.solnumero,
  })

  // Garantizamos que SIEMPRE sea un arreglo antes de que llegue al render
  const trazaData = Array.isArray(rawTrazaData)
    ? rawTrazaData
    : rawTrazaData?.data && Array.isArray(rawTrazaData.data)
      ? rawTrazaData.data
      : []

  const isLoadingGlobal = loadSol || loadTraza

  // ABRIR EL MODAL CON LOS DATOS DE LA TRANSACCIÓN
  const handleOpenPrintPreview = (transaccion, event) => {
    event.stopPropagation() // Evita que el click colapse el Accordion
    setTransaccionSeleccionada(transaccion)
    setModalImpresionOpen(true)
  }

  const handleClosePrintPreview = () => {
    setModalImpresionOpen(false)
    setTimeout(() => setTransaccionSeleccionada(null), 300)
  }

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 30px 30px 30px",
            fontSize: "25px",
            gap: "15px",
          }}
        >
          <HistoryIcon fontSize="large" color="primary" />
          <b>Trazabilidad de Solicitud: {state?.solnumero}</b>
          {solData?.sgastatus && (
            <Chip
              label={solData.sgastatus}
              color={
                ["ENT.TOT.", "CERRADA", "NEGADA"].includes(solData.sgastatus)
                  ? "error"
                  : ["EN PRESTAMO", "ENT.PAR.", "EN TRANSITO"].includes(solData.sgastatus)
                    ? "info"
                    : "success"
              }
              sx={{ fontWeight: "bold", fontSize: "1rem" }}
            />
          )}
        </div>

        <CustomBackdrop isLoading={isLoadingGlobal} />

        <Box sx={StyledRoot}>
          {/* ============================================================ */}
          {/* BLOQUE 1: ESTADO ACTUAL DE LA SOLICITUD                      */}
          {/* ============================================================ */}
          <Paper elevation={3} sx={{ p: 4, borderRadius: 3, background: "white", mb: 4 }}>
            <Typography variant="h6" color="primary" gutterBottom>
              Resumen de la Solicitud
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={3}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  disabled
                  label="Tipo Solicitud"
                  value={solData?.tsolInEg || ""}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  disabled
                  label="Localidad Solicita"
                  value={solData?.locsolicita || "N/A"}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  disabled
                  label="Bodega Origen (Egreso)"
                  value={solData?.bodegreso || "N/A"}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  disabled
                  label="Bodega Destino (Ingreso)"
                  value={solData?.bodingreso || "N/A"}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  disabled
                  multiline
                  label="Observación / Descripción Original"
                  value={solData?.sgadescri || ""}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 4 }} />

            <Typography variant="h6" color="primary" gutterBottom>
              Detalle Original y Acumulados
            </Typography>

            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead sx={{ backgroundColor: "#f5f7fa" }}>
                  <TableRow>
                    <TableCell width="5%">#</TableCell>
                    <TableCell width="15%">Código</TableCell>
                    <TableCell width="35%">Descripción</TableCell>
                    <TableCell width="15%" align="center">
                      Cant. Solicitada
                    </TableCell>
                    <TableCell width="15%" align="center">
                      Total Despachado
                    </TableCell>
                    <TableCell width="15%" align="center">
                      Total Recibido
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {solData?.detalle?.length > 0 ? (
                    solData.detalle.map((row, index) => (
                      <TableRow key={index}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell sx={{ fontWeight: "bold" }}>{row.artcodigo}</TableCell>
                        <TableCell>{row.artdescri || "N/A"}</TableCell>
                        <TableCell align="center">{row.sgacansol}</TableCell>
                        <TableCell align="center" sx={{ color: "error.main", fontWeight: "bold" }}>
                          {row.sgacanegr || 0}
                        </TableCell>
                        <TableCell align="center" sx={{ color: "primary.main", fontWeight: "bold" }}>
                          {row.sgacanrec || 0}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        No hay detalles disponibles.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* ============================================================ */}
          {/* BLOQUE 2: LÍNEA DE TIEMPO (DESPLEGABLES POR TRANSACCIÓN)     */}
          {/* ============================================================ */}
          <Paper elevation={3} sx={{ p: 4, borderRadius: 3, background: "white" }}>
            <Typography variant="h6" color="primary" gutterBottom sx={{ mb: 3 }}>
              Historial de Transacciones (Línea de Tiempo)
            </Typography>

            {(!trazaData || trazaData.length === 0) && !isLoadingGlobal ? (
              <Typography
                variant="body1"
                color="textSecondary"
                sx={{ fontStyle: "italic", textAlign: "center", py: 4 }}
              >
                Esta solicitud aún no tiene transacciones de inventario procesadas.
              </Typography>
            ) : (
              <Box sx={{ ml: { xs: 1, md: 4 }, position: "relative" }}>
                <Box
                  sx={{
                    position: "absolute",
                    left: "15px",
                    top: "20px",
                    bottom: "20px",
                    width: "3px",
                    backgroundColor: "#e0e0e0",
                    zIndex: 0,
                  }}
                />

                {trazaData?.map((tra, index) => {
                  const isIngreso = String(tra.tratipo).toUpperCase() === "INGRESO"
                  const nodeColor = isIngreso ? theme.palette.primary.main : theme.palette.error.main

                  return (
                    <Box key={index} sx={{ position: "relative", mb: 2, pl: 6, zIndex: 1 }}>
                      {/* Punto Indicador */}
                      <Box
                        sx={{
                          position: "absolute",
                          left: "6px",
                          top: "14px",
                          width: "20px",
                          height: "20px",
                          borderRadius: "50%",
                          backgroundColor: nodeColor,
                          border: "4px solid white",
                          boxShadow: "0 0 0 2px #e0e0e0",
                          zIndex: 2,
                        }}
                      />

                      {/* Componente Desplegable (Acordeón) */}
                      <Accordion
                        variant="outlined"
                        defaultExpanded={false}
                        sx={{ borderColor: "#e0e0e0", borderRadius: 2, "&:before": { display: "none" } }}
                      >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Box sx={{ display: "flex", flexDirection: "column" }}>
                            <Typography
                              variant="subtitle1"
                              sx={{
                                color: nodeColor,
                                fontWeight: "bold",
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              [{tra.tratipo}] {tra.tranombre || "TRANSACCIÓN"}
                            </Typography>
                            <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                              <b>Nro:</b> {tra.tranumero} &nbsp;|&nbsp; <b>Fecha:</b> {formatFechaVB6(tra.trafecha)}{" "}
                              {formatHora(tra.trahora || tra.trahorisys)} &nbsp;|&nbsp; <b>Bodega:</b> {tra.bodcodigo}
                            </Typography>
                          </Box>
                        </AccordionSummary>

                        <AccordionDetails sx={{ borderTop: "1px solid #e0e0e0", pt: 2 }}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              mb: 2,
                              flexWrap: "wrap",
                              gap: 2,
                            }}
                          >
                            <Typography variant="body2" color="textSecondary">
                              <b>Usuario Proceso:</b> {tra.trausuisys}
                            </Typography>
                            <Button
                              variant="outlined"
                              color={isIngreso ? "primary" : "error"}
                              startIcon={<PrintIcon />}
                              onClick={(e) => handleOpenPrintPreview(tra, e)}
                              size="small"
                            >
                              Imprimir Comprobante
                            </Button>
                          </Box>

                          <TableContainer sx={{ border: "1px solid #f0f0f0", borderRadius: 1 }}>
                            <Table size="small">
                              <TableHead sx={{ backgroundColor: isIngreso ? "#e3f2fd" : "#ffebee" }}>
                                <TableRow>
                                  <TableCell>Código</TableCell>
                                  <TableCell>Descripción del Artículo</TableCell>
                                  <TableCell align="right">Cant. Procesada</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {tra.detalle?.map((item, idx) => (
                                  <TableRow key={idx}>
                                    <TableCell>{item.artcodigo}</TableCell>
                                    <TableCell>{item.artdescri}</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: "bold" }}>
                                      {Number(item.tracantidad).toFixed(2)}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </AccordionDetails>
                      </Accordion>
                    </Box>
                  )
                })}
              </Box>
            )}
          </Paper>
        </Box>

        {/* ============================================================ */}
        {/* MODAL DE VISTA PREVIA E IMPRESIÓN DE DOCUMENTO VB6           */}
        {/* ============================================================ */}
        <Dialog
          open={modalImpresionOpen}
          onClose={handleClosePrintPreview}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: { minHeight: "80vh", backgroundColor: "#525659" } }}
        >
          <DialogTitle
            sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", backgroundColor: "white" }}
          >
            <Typography variant="h6" fontWeight="bold">
              Vista Previa de Impresión
            </Typography>
            <IconButton onClick={handleClosePrintPreview} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <Divider />

          <DialogContent sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <Box
              sx={{
                width: "210mm",
                minHeight: "297mm",
                backgroundColor: "white",
                boxShadow: "0px 0px 10px rgba(0,0,0,0.5)",
                overflow: "hidden",
              }}
            >
              <div ref={componentRef}>
                <ReporteComprobanteVB6
                  transaccion={transaccionSeleccionada}
                  ciaNombre={ciaNombreActual}
                  solnumero={state?.solnumero}
                />
              </div>
            </Box>
          </DialogContent>

          <DialogActions sx={{ backgroundColor: "white", p: 2 }}>
            <Button onClick={handleClosePrintPreview} color="inherit" sx={{ fontWeight: "bold" }}>
              Cancelar
            </Button>
            <Button
              onClick={handlePrint}
              variant="contained"
              color="primary"
              startIcon={<PrintIcon />}
              sx={{ px: 4, fontWeight: "bold" }}
            >
              Confirmar Impresión
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </ThemeProvider>
  )
}

export default BuscarSolicitudInv
